import React, { memo, useCallback, useEffect, useMemo, useReducer } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Input } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';
import ModalOptions from 'components/ModalOptions';

import useExportImplementationsModal from './useExportImplementationsModal';
import { initialState, reducer } from './reducer';
import styles from './styles';

const useStyles = createUseStyles(styles, 'ExportImplementationsModal');

const ExportImplementationsModal = memo(({ onRef }) => {
    const classes = useStyles();
    const { t, email: userEmail } = useWeb5Context();
    const [state, dispatch] = useReducer(reducer, initialState);

    const { initialize, exportImplementations } = useExportImplementationsModal({
        dispatch,
        state,
        t,
        userEmail,
    });

    const onClose = useCallback(() => {
        dispatch({ type: 'closeModal' });
    }, []);

    const onChangeSelected = useCallback((data) => {
        const numSelected = data.fields?.options?.length || 0;
        dispatch({
            type: 'setSelected',
            selected: data,
            numSelected,
        });
    }, []);

    const onChangeEmail = useCallback((email = '') => {
        dispatch({ type: 'setEmail', email });
    }, []);

    useEffect(() => {
        onRef?.({ open: initialize });
    }, [onRef, initialize]);

    const postComponent = useMemo(
        () => (
            <Input
                placeholder={t('cfm_placeholder_type_email')}
                onChange={onChangeEmail}
                value={state.email}
                isFullWidth
                label={t('label_email')}
                labelMode="vertical"
                error={state.validEmail ? undefined : t('label_invalid_email')}
                type="email"
            />
        ),
        [onChangeEmail, state.email, state.validEmail, t],
    );

    const modalOptionsProps = useMemo(
        () => ({
            schema: state.fieldsSchema,
            title: t('cfm_title_export'),
            className: classes.root,
            overlayClassName: classes.overlay,
            isOpen: state.isOpen,
            adviceType: 'warning',
            advice: t('label_export_switched_to_email'),
            onCancel: onClose,
            onRequestClose: onClose,
            onConfirm: exportImplementations,
            onChangeSelected,
            cancelText: t('cfm_action_cancel'),
            confirmText: t('action_send_email'),
            placeholderSearch: t('label_search_field'),
            overrides: {
                confirmButton: {
                    isLoading: state.isLoading,
                    isDisabled: !state.numSelected,
                },
            },
            isLoading: !state.optionsIsReady,
            postComponent,
        }),
        [
            classes.overlay,
            classes.root,
            onChangeSelected,
            onClose,
            exportImplementations,
            postComponent,
            state.fieldsSchema,
            state.isLoading,
            state.isOpen,
            state.numSelected,
            state.optionsIsReady,
            t,
        ],
    );

    return <ModalOptions {...modalOptionsProps} selected={state.selected} />;
});

ExportImplementationsModal.displayName = 'ExportImplementationsModal';

ExportImplementationsModal.propTypes = {
    onRef: PropTypes.func,
};

export default ExportImplementationsModal;
